import React, { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    CircularProgress,
    Box,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import useDutiesWeeksService from '../../api/services/DutiesWeeksService';
import useTermService from '../../api/services/termService';
import toast from 'react-hot-toast';

const DutiesWeekSchema = Yup.object().shape({
    weekName: Yup.string().required('Week name is required'),
    weekStartDate: Yup.date().required('Week start date is required'),
    weekEndDate: Yup.date()
        .required('Week end date is required')
        .min(Yup.ref('weekStartDate'), 'End date must be after start date'),
    termId: Yup.string().required('Term is required'),
});

const EditDialog = ({ dutiesWeekId, open, onClose, onSuccess }) => {
    const [initialData, setInitialData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const { loadDutiesWeekDetails, updateDutiesWeekDetails } = useDutiesWeeksService();
    const { fetchAllActiveTerms, terms } = useTermService();

    useEffect(() => {
        if (open) {
            fetchAllActiveTerms(0, 100, 'ACTIVE');
        }
    }, [open]);

    useEffect(() => {
        const fetchData = async () => {
            if (!open || !dutiesWeekId) {
                setInitialData(null);
                return;
            }

            setLoading(true);
            try {
                const week = await loadDutiesWeekDetails(dutiesWeekId);
                // Parse dates - backend returns "yyyy-MM-dd HH:mm:ss" format
                const startDate = week.weekStartDate 
                    ? (dayjs(week.weekStartDate).isValid() ? dayjs(week.weekStartDate) : dayjs(week.weekStartDate.replace(' ', 'T')))
                    : dayjs();
                const endDate = week.weekEndDate 
                    ? (dayjs(week.weekEndDate).isValid() ? dayjs(week.weekEndDate) : dayjs(week.weekEndDate.replace(' ', 'T')))
                    : dayjs().add(7, 'day');
                
                setInitialData({
                    id: week.id,
                    weekName: week.weekName || '',
                    weekStartDate: startDate,
                    weekEndDate: endDate,
                    termId: week.termId || '',
                });
            } catch (error) {
                console.error('Failed to load duties week data:', error);
                toast.error('Failed to load duties week data. Please try again.');
                setInitialData(null);
            } finally {
                setLoading(false);
            }
        };

        if (open && dutiesWeekId) {
            fetchData();
        } else {
            setInitialData(null);
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, dutiesWeekId]);

    const handleSave = async (values) => {
        if (!initialData) return;

        setSaving(true);
        try {
            // Ensure dates are valid dayjs objects before formatting
            const startDate = dayjs(values.weekStartDate);
            const endDate = dayjs(values.weekEndDate);

            if (!startDate.isValid() || !endDate.isValid()) {
                toast.error('Invalid date values. Please check your dates.');
                setSaving(false);
                return;
            }

            const payload = {
                id: initialData.id,
                weekName: values.weekName,
                weekStartDate: startDate.format('YYYY-MM-DD') + 'T00:00:00',
                weekEndDate: endDate.format('YYYY-MM-DD') + 'T23:59:59',
                termId: values.termId,
            };

            console.log('Saving payload:', payload);
            await updateDutiesWeekDetails(payload);
            toast.success('Duties week updated successfully');
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Failed to update duties week:', error);
            toast.error('Failed to update duties week. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>EDIT DUTIES WEEK</DialogTitle>

            <DialogContent>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : initialData ? (
                    <Formik
                        enableReinitialize
                        initialValues={initialData}
                        validationSchema={DutiesWeekSchema}
                        onSubmit={handleSave}
                    >
                        {({
                            values,
                            errors,
                            touched,
                            handleChange,
                            handleBlur,
                            handleSubmit,
                            setFieldValue,
                        }) => (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2} mt={1}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            variant="filled"
                                            label="Week Name"
                                            name="weekName"
                                            value={values.weekName || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.weekName && !!errors.weekName}
                                            helperText={touched.weekName && errors.weekName}
                                            disabled={saving}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            variant="filled"
                                            label="Term"
                                            name="termId"
                                            value={values.termId || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            error={touched.termId && !!errors.termId}
                                            helperText={touched.termId && errors.termId}
                                            disabled={saving || !terms || terms.length === 0}
                                        >
                                            {terms && terms.length > 0 ? (
                                                terms.map(term => (
                                                    <MenuItem key={term.id} value={term.id}>
                                                        {term.termName}
                                                    </MenuItem>
                                                ))
                                            ) : (
                                                <MenuItem value="" disabled>
                                                    No terms available
                                                </MenuItem>
                                            )}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileDatePicker
                                                label="Week Start Date"
                                                value={values.weekStartDate}
                                                onChange={(value) => {
                                                    console.log('Start date changed:', value);
                                                    setFieldValue('weekStartDate', value, true);
                                                }}
                                                disabled={saving}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        variant: 'filled',
                                                        error: touched.weekStartDate && !!errors.weekStartDate,
                                                        helperText: touched.weekStartDate && errors.weekStartDate,
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                            <MobileDatePicker
                                                label="Week End Date"
                                                minDate={values.weekStartDate}
                                                value={values.weekEndDate}
                                                onChange={(value) => {
                                                    console.log('End date changed:', value);
                                                    setFieldValue('weekEndDate', value, true);
                                                }}
                                                disabled={saving}
                                                slotProps={{
                                                    textField: {
                                                        fullWidth: true,
                                                        variant: 'filled',
                                                        error: touched.weekEndDate && !!errors.weekEndDate,
                                                        helperText: touched.weekEndDate && errors.weekEndDate,
                                                    },
                                                }}
                                            />
                                        </LocalizationProvider>
                                    </Grid>
                                </Grid>

                                <DialogActions sx={{ mt: 3 }}>
                                    <Button 
                                        onClick={onClose} 
                                        color="secondary" 
                                        variant="contained"
                                        disabled={saving}
                                    >
                                        Cancel
                                    </Button>
                                    <Button 
                                        type="submit" 
                                        color="secondary" 
                                        variant="contained"
                                        disabled={saving}
                                    >
                                        {saving ? <CircularProgress size={24} /> : 'Save'}
                                    </Button>
                                </DialogActions>
                            </form>
                        )}
                    </Formik>
                ) : (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <TextField
                            fullWidth
                            variant="filled"
                            label="No data available"
                            disabled
                            helperText="Unable to load duties week data"
                        />
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default EditDialog;
